/**
 * Lecture d'une ancienne facture ou offre, pour la reprendre dans SwissTech.
 *
 * Le texte vient d'un copier-coller ou d'un PDF lu par le serveur, colonnes
 * conservées. On en tire le client, les lignes (désignation, quantité, unité,
 * prix, rabais), la date et le numéro d'origine.
 *
 * Les logiciels posent souvent les chiffres d'une ligne à mi-hauteur de sa
 * désignation, quand celle-ci a une seconde ligne : la désignation et ses
 * chiffres arrivent alors sur deux lignes de texte, qu'il faut recoller.
 */

const UNITES =
  'm²|m2|m³|m3|ml|m|h|heures?|pce|pcs|pièces?|pi[eè]ces?|sacs?|kg|t|forfaits?|fft|unités?|u|' +
  'litres?|l|cartons?|palettes?|jours?|j|lots?|ens|st|stk|rouleaux?|boîtes?|paquets?|' +
  // Les abréviations des factures de fournisseurs (HGC, Gétaz…) : « 3 BTE »
  // (boîte), « 6 RLX » (rouleau) se lisaient comme une partie du nom.
  'btes?|rlx|rl|rol|crt|ctn|pal|paq|pqt|fl|bid|seaux?|tubes?|paires?|lm|kgs|' +
  // … et en allemand : « 3 KAR » (Karton), « 2 ROL », « 4 SACK ».
  'kar|ktn|sack|pak|rolle|dose|eimer';
// Les milliers se séparent par une seule espace ou une apostrophe
// (« 1 600.00 », « 1'600.00 ») : plusieurs espaces séparent deux colonnes, et
// « 60        6.85 » (quantité, prix) se lisait 606.85.
const MONTANT = "-?\\d{1,3}(?:['’ ]\\d{3})+[.,]\\d{2}|-?\\d+[.,]\\d{2}";
const LIGNE = new RegExp(
  '^(.*?)\\s*(\\d+(?:[.,]\\d+)?)\\s*(' +
    UNITES +
    ')?\\s+(?:CHF\\s*)?(' +
    MONTANT +
    ')(?:\\s+(—|–|-|\\d+(?:[.,]\\d+)?\\s*%))?(?:\\s+(?:CHF\\s*)?(' +
    MONTANT +
    '))?\\s*$',
  'i',
);
// Le prix donné pour une unité de prix (colonnes « Prix PE UPE ») : « 25 KG
// 2.69 1 KG 67.25 », « 40 PCE 7.40 / 100 PCE 2.96 ». Le « 1 KG » se lisait
// comme la quantité et le montant comme le prix : « … 25 KG 2.69 (1 kg) ».
const PAR_UNITE_DE_PRIX = new RegExp(
  '^(.*?)\\s+(\\d+(?:[.,]\\d+)?)\\s*(' +
    UNITES +
    ')\\s+(\\d+[.,]\\d{2,4})\\s+(?:\\/\\s*)?(\\d{1,4})\\s*(' +
    UNITES +
    ')(?:\\s+(\\d+(?:[.,]\\d+)?)\\s*%)?\\s+(' +
    MONTANT +
    ')(?:\\s+[A-Z0-9]{1,2})?\\s*$',
  'i',
);

/**
 * La ligne d'une facture de fournisseur dont le prix vaut pour une unité de
 * prix (1 KG, 100 PCE), ou null. Le montant confirme la lecture.
 *
 * @param {string} ligne ligne du tableau
 * @returns {{d: string, q: number, u: string, p: number, rem: number}|null} la ligne lue
 */
export function lisLigneParUniteDePrix(ligne) {
  const m = String(ligne || '').match(PAR_UNITE_DE_PRIX);
  if (!m) return null;
  const q = nombre(m[2]);
  const prix = nombre(m[4]);
  const pe = nombre(m[5]) || 1;
  const rem = m[7] ? nombre(m[7]) : 0;
  const montant = nombre(m[8]);
  if (!(q > 0) || !(prix > 0) || !egal((q * prix * (1 - rem / 100)) / pe, montant)) return null;
  const d = sansCodesEnTete(designation(m[1]));
  if (d.length < 2 || !/\p{L}{2}/u.test(d)) return null;
  return { d, q, u: m[3].toLowerCase(), p: Math.round((prix / pe) * 10000) / 10000, rem };
}

/**
 * Le nom d'un article sans la suite de numéros qui le précède : « 10 100142373
 * 28030 kerakoll H40 » (position, article, référence du fabricant) devient
 * « 100142373 kerakoll H40 » — le numéro d'article reste, pour recommander.
 *
 * @param {string} d désignation lue
 * @returns {string} la désignation
 */
export function sansCodesEnTete(d) {
  const mots = String(d || '').split(' ');
  let n = 0;
  while (n < mots.length && /^\d+$/.test(mots[n])) n += 1;
  if (n < 2 || n >= mots.length) return d;
  const article = mots.slice(0, n).find((m) => m.length >= 5);
  return [article, ...mots.slice(n)].filter(Boolean).join(' ');
}

const HORS_LIGNES =
  /sous-total|^total|\btotal\s+(ht|ttc|net)|^(tva|mwst|iva|vat)\b|\b(tva|mwst|iva|vat)\s+\d|acompte|montant|échéance|iban|référence|r[ée]f\.?\s*:|rabais lignes|à payer|solde/i;
const EN_TETE_TABLEAU =
  /d[ée]signation|description|libell[ée]|prestation|article|texte|travaux|d[ée]tail|objet/i;
// La ligne « NPA localité » d'une adresse. Les anciens logiciels l'écrivent
// de bien des façons : « CH-1228 Plan-les-Ouates », « 1260 Nyon, Suisse »,
// « 1003 Lausanne (VD) », ou un client français « 74100 Annemasse » ; seule la
// forme « 1228 Plan-les-Ouates » était reconnue, et le client d'une partie des
// factures reprises arrivait sans coordonnées.
// La localité peut finir par un numéro d'arrondissement : « 1215 Genève 15 ».
const NPA = /^(?:(?:CH|FL|LI|F|D|I|A)\s?[-–]?\s?)?(\d{4,5})\s+([A-Za-zÀ-ÿ][^\d]*(?:\s\d{1,2})?)$/i;
/** Le code postal d'une ligne d'adresse, ou '' si ce n'en est pas une. */
const codePostal = (ligne) =>
  (String(ligne || '')
    .trim()
    .match(NPA) || [])[1] || '';

const nombre = (s) =>
  parseFloat(
    String(s || '')
      .replace(/['’\s\u00a0]/g, '')
      .replace(',', '.'),
  ) || 0;

/** Désignation sans numéro de position ni étiquette d'en-tête. */
const designation = (texte) =>
  String(texte || '')
    // L'apostrophe que certains PDF rendent en « modificateur » (ʼ).
    .replace(/\u02bc/g, '’')
    .replace(/^\s*\d{1,3}[.)]?\s+(?=\S)/, '')
    .replace(/^(URGENT|OPTION|OPTIONNEL)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();

const UNITE_SEULE = new RegExp('^(' + UNITES + ')$', 'i');
const UNITE_FINALE = new RegExp('\\s(' + UNITES + ')$', 'i');
const NOMBRE_SOUPLE = /^-?\d[\d'’]*(?:[.,]\d+)?(?:[.,][-–—])?$/;

/** Un montant écrit à la suisse : « 1'850.00 », « 350.– », « 42,5 ». */
const valeur = (jeton) => nombre(jeton.replace(/[.,][-–—]$/, ''));

/** Total de la pièce : ce qui suit n'est plus le tableau, sauf ligne confirmée. */
const TOTAL_FINAL = /^(montant\s+)?total\b(?!\s*report)|net\s+à\s+payer|^à\s+payer/i;

/** Un prix tel qu'on l'écrit sur une pièce : « 350.00 », « 42,50 », « 350.– ». */
const FORME_DE_PRIX = /[.,](\d{2}|[-–—])$/;

/**
 * Lignes d'en-tête ou de pied qui portent deux nombres sans être des
 * prestations : coordonnées, conditions, horaires.
 */
const PAS_UNE_PRESTATION =
  /(?<!\p{L})(t[ée]l|fax|natel|mobile|validit[ée]|d[ée]lai|payable|rabais|escompte|arrondi|horaires?)(?!\p{L})/iu;

/** Vrai si a et b se valent, à un centime ou un pour cent près. */
const egal = (a, b) => Math.abs(a - b) <= Math.max(0.05, Math.abs(b) * 0.01);

/**
 * Une ligne mise au propre pour la lecture des chiffres. Certains logiciels
 * (Invoice2go…) écrivent « 1 600.00 CHF » : la devise après le montant et une
 * espace entre les milliers, que la lecture prenait pour deux nombres.
 */
const auPropre = (ligne) =>
  ligne
    .replace(/(^|\s)(CHF|EUR|Fr\.)(?=\s|$)|€/gi, '$1 ')
    // Seulement en début de colonne (après deux espaces ou plus, ou au début) :
    // « 1    1 600.00 » est un prix de 1600, « Plâtrerie 2 150.00 », 2 × 150.
    .replace(/(^|[ \u00a0]{2,})(-?\d{1,3})[ \u00a0](?=\d{3}(?:[.,]\d{1,2})?(?!\d))/g, '$1$2')
    .replace(/[ \u00a0]+$/, '');

/** Pied de page répété sur chaque page : « 1 / 3 », « - Devis 44 - 04.10.2026 ». */
const PIED_DE_PAGE =
  /^(\d+\s*\/\s*\d+|-?\s*(devis|offre|facture)\b.*\d{1,2}[./]\d{1,2}[./]\d{2,4}\s*-?)$/i;

/** Numéro d'article d'un fournisseur : chiffres et capitales, sans mot. */
const CODE_ARTICLE = /^(?=[^a-z]*\d{3})[A-Z0-9][A-Z0-9.\-/]*$/;

/** Première lettre en minuscule, pour recoller « Plafond » et « Existant ». */
const enMinuscule = (t) => (/^\p{Lu}\p{Ll}/u.test(t) ? t[0].toLowerCase() + t.slice(1) : t);

/**
 * Une ligne que le motif strict n'a pas reconnue : prix sans centimes
 * (« 350.– », « 350 »), « CHF » ou « Fr. » entre les colonnes, unité avant la
 * quantité. Les chiffres de la fin se lisent de droite à gauche ; trois
 * chiffres ne valent une ligne que si quantité × prix donne le total, ce qui
 * écarte les téléphones, dates et numéros de l'en-tête.
 *
 * @param {string} ligne ligne du tableau
 * @param {boolean} dansTableau vrai sous l'en-tête du tableau
 * @returns {{d: string, q: number, u: string, p: number, rem: number}|null} la ligne lue
 */
function lisLigneSouple(ligne, dansTableau, apresTotal = false) {
  const jetons = ligne
    .replace(/\b(CHF|Fr\.)\s*/gi, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const nombres = [];
  const formes = [];
  let unite = '';
  let rem = 0;
  let i = jetons.length - 1;
  let iUnite = -1;
  let nombresAvantUnite = 0;
  for (; i >= 0; i -= 1) {
    const j = jetons[i];
    if (NOMBRE_SOUPLE.test(j) && iUnite >= 0 && nombresAvantUnite >= 3) {
      // « Enduit 25 kg   12   32.50   390.00 » : l'unité suit un nombre, elle
      // est du nom (« 25 kg »), non de la quantité.
      i = iUnite;
      unite = '';
      break;
    } else if (NOMBRE_SOUPLE.test(j)) {
      nombres.unshift(valeur(j));
      formes.unshift(j);
    } else if (/^\d+(?:[.,]\d+)?%$/.test(j)) rem = nombre(j);
    else if (j === '%' && i > 0 && NOMBRE_SOUPLE.test(jetons[i - 1])) {
      rem = valeur(jetons[i - 1]);
      i -= 1;
    } else if (/^[-–—]$/.test(j)) continue;
    else if (UNITE_SEULE.test(j) && !unite) {
      unite = j.toLowerCase();
      iUnite = i;
      nombresAvantUnite = nombres.length;
    } else break;
  }
  const d = designation(jetons.slice(0, i + 1).join(' '));
  const ligneDe = (q, p) => (q > 0 && p !== 0 ? { d, q, u: unite || 'pce', p, rem } : null);
  if (nombres.length >= 3) {
    // Quantité, prix, total — le total confirme la lecture.
    for (let k = nombres.length - 3; k >= 0; k -= 1) {
      const [q, p, total] = [nombres[k], nombres[k + 1], nombres[nombres.length - 1]];
      if (egal(q * p * (1 - rem / 100), total)) return ligneDe(q, p);
      // Le pourcentage était la TVA de la ligne, pas un rabais.
      if (rem && egal(q * p, total)) {
        rem = 0;
        return ligneDe(q, p);
      }
    }
    return null;
  }
  if (apresTotal) return null;
  // Deux nombres seulement, sans total pour confirmer : il faut une unité ou un
  // prix écrit comme tel, et pas un téléphone, un délai ou des horaires.
  if (
    nombres.length === 2 &&
    (unite || (dansTableau && formes.some((f) => FORME_DE_PRIX.test(f)))) &&
    !PAS_UNE_PRESTATION.test(d)
  ) {
    const [a, b] = nombres;
    // Prix et total égaux : une seule unité.
    if (egal(a, b)) return ligneDe(1, a);
    return ligneDe(a, b);
  }
  return null;
}

/**
 * Les lignes de prestation d'un texte.
 *
 * @param {string[]} lignes lignes du tableau
 * @param {boolean} [dansTableau] vrai quand l'en-tête du tableau a été trouvé
 * @returns {{d: string, sd: string, q: number, u: string, p: number, rem: number}[]} les prestations
 */
export function lisLignes(
  lignes,
  dansTableau = false,
  { colonne = 0, colPosition = null, repetees = new Set() } = {},
) {
  const articles = [];
  let enAttente = null; // désignation dont les chiffres sont sur la ligne suivante
  // Le titre en attente commence par un numéro de position (« 14 Robinetterie… ») :
  // le texte écrit sur la ligne des chiffres est alors sa description.
  // SwissTech, comme bien des logiciels, pose les chiffres à mi-hauteur
  // d'une prestation décrite : le titre était perdu, remplacé par le début
  // de la description.
  let titreNumerote = false;
  let descriptionAttente = ''; // description écrite entre le titre et ses chiffres
  /**
   * Titre et description d'une ligne de chiffres qui porte aussi du texte.
   * Une suite en minuscule (« l'italienne et pourtour) ») achève le titre.
   */
  const nettes = lignes.map((l) => auPropre(String(l || '').replace(/\u00a0/g, ' ')).trim());
  /** Le texte d'une ligne de chiffres, ou null si la ligne n'en porte pas. */
  const texteDesChiffres = (ligne) => {
    const mm = ligne.match(LIGNE);
    if (mm) return designation(mm[1]);
    const s = lisLigneSouple(ligne, dansTableau);
    return s ? s.d : null;
  };
  /** La ligne suivante, non vide. */
  const suivante = (i) => {
    for (let j = i + 1; j < nettes.length; j += 1) if (nettes[j]) return j;
    return -1;
  };
  /** Une ligne de texte seul qui commence en minuscule : la suite d'une phrase. */
  const suiteEnMinuscule = (i) => {
    const j = suivante(i);
    return j >= 0 && texteDesChiffres(nettes[j]) === null && /^\p{Ll}/u.test(nettes[j]);
  };
  /**
   * Le texte d'une ligne de chiffres est-il une description plutôt qu'un
   * titre ? Une phrase : ponctuée, ou poursuivie en minuscule à la ligne.
   * Les logiciels qui centrent les chiffres dans la hauteur d'une prestation
   * les posent sur sa description, le titre juste au-dessus.
   */
  const estUneDescription = (texte, i) => !!texte && (/[.;,]$/.test(texte) || suiteEnMinuscule(i));
  /**
   * La ligne de texte seul ouvre-t-elle la prestation suivante ? Oui quand,
   * sa suite en minuscule passée, viennent des chiffres sans texte ou posés
   * sur une description.
   */
  const ouvreUnTitre = (i) => {
    let j = suivante(i);
    while (j >= 0 && texteDesChiffres(nettes[j]) === null && /^\p{Ll}/u.test(nettes[j]))
      j = suivante(j);
    // Une ligne numérotée (« 5 Étanchéité… ») porte son propre titre.
    if (j < 0) return false;
    const t = texteDesChiffres(nettes[j]);
    if (t && /^\d{1,3}[.)]?\s+\S/.test(nettes[j])) return false;
    return t === '' || (t !== null && estUneDescription(t, j));
  };
  let indice = -1;
  const titreEtDetail = (texte, numerotee = false) => {
    // Une ligne de chiffres numérotée dans la colonne « Position » porte son titre.
    if (!enAttente || (numerotee && texte)) return { d: texte, sd: '' };
    if (!texte) return { d: enAttente, sd: descriptionAttente };
    if (!titreNumerote) {
      return estUneDescription(texte, indice) ? { d: enAttente, sd: texte } : { d: texte, sd: '' };
    }
    if (/^\d{1,3}(?:[.)]|[.]\d)/.test(texte)) return { d: texte, sd: '' };
    if (!descriptionAttente && /^\p{Ll}/u.test(texte))
      return { d: enAttente + ' ' + texte, sd: '' };
    return { d: enAttente, sd: [descriptionAttente, texte].filter(Boolean).join(' ') };
  };
  /**
   * Ajoute une ligne au détail : une puce ou un point numéroté (« ◦ Pose… »,
   * « 2. Lavabo ») commence une ligne, comme sur la pièce ; le reste suit.
   */
  let retraitPrecedent = -1;
  const ajouteDetail = (detail, texte, retrait = retraitPrecedent) => {
    const t = texte.replace(/\s+/g, ' ');
    // Un nouveau paragraphe, moins ou plus en retrait que la ligne d'avant.
    // Une suite en minuscule reste dans la phrase.
    const autreRetrait =
      retraitPrecedent >= 0 && Math.abs(retrait - retraitPrecedent) > 3 && !/^\p{Ll}/u.test(t);
    retraitPrecedent = retrait;
    if (!detail) return t;
    const saut = autreRetrait || /^(?:[◦•▪‣·*–-]|\d{1,2}[.)])\s/u.test(t);
    return detail + (saut ? '\n' : ' ') + t;
  };
  const oublieLAttente = () => {
    enAttente = null;
    titreNumerote = false;
    descriptionAttente = '';
  };
  let derniere = null; // dernière prestation, qui reçoit les lignes de détail
  let apresTotal = false; // le total est passé : vient le pied de page
  // Colonne « Réf. » à gauche de la désignation (Invoice2go…) : le titre de la
  // prestation commence à gauche et se poursuit sur les lignes suivantes,
  // le détail à droite. Les deux se séparent à la colonne de la désignation.
  const coupe = (brute) => {
    if (!colonne || brute.length <= colonne || /\S/.test(brute[colonne - 1] || '')) return null;
    const gauche = brute.slice(0, colonne).trim();
    // Une colonne « Pos. » (« 3 ») n'est pas un titre.
    if (!gauche || /^\d{1,3}[.)]?$/.test(gauche)) return null;
    return { gauche, droite: brute.slice(colonne).trim() };
  };
  for (const brute0 of lignes) {
    indice += 1;
    const brute = brute0.replace(/\u00a0/g, ' ');
    const ligne = auPropre(brute).trim();
    if (!ligne) continue;
    if (PIED_DE_PAGE.test(ligne)) continue;
    // En-tête et pied répétés à chaque page (« Facture F2026-014 Page 2 de 3 »,
    // coordonnées de l'entreprise) : ils se glissaient dans la description.
    if (/\bpage\s+\d+\s*(?:de|sur|\/|of|von)\s*\d+\b/i.test(ligne)) continue;
    if (repetees.has(ligne) && texteDesChiffres(ligne) === null) continue;
    // Un numéro dans la colonne « Position » ouvre une prestation ; plus à
    // droite, c'est une liste de la description (« 1. Douche », « 2. Lavabo »).
    const retrait = brute.length - brute.trimStart().length;
    const dansLaColonnePosition = colPosition === null || retrait <= colPosition + 3;
    const numerotee = dansLaColonnePosition && /^\d{1,3}[.)]?\s+\S/.test(ligne);
    // L'en-tête du tableau, répété en haut de chaque page.
    if (EN_TETE_TABLEAU.test(ligne) && /qt|quantit|p\.u|prix/i.test(ligne)) {
      oublieLAttente();
      continue;
    }
    if (HORS_LIGNES.test(ligne)) {
      if (TOTAL_FINAL.test(ligne)) apresTotal = true;
      oublieLAttente();
      derniere = null;
      continue;
    }
    const parPe = lisLigneParUniteDePrix(ligne);
    if (parPe) {
      const { d, sd } = titreEtDetail(parPe.d, numerotee);
      derniere = { ...parPe, d, sd };
      articles.push(derniere);
      oublieLAttente();
      continue;
    }
    const m0 = ligne.match(LIGNE);
    // Une mesure du nom (« 60×60 », « 160×90 ») n'est pas une quantité.
    const m = m0 && !/[×xX*/]$/.test(m0[1]) ? m0 : null;
    const souple = m ? null : lisLigneSouple(ligne, dansTableau, apresTotal);
    // Un forfait : la désignation, puis le montant seul, dans sa colonne.
    const forfait =
      !m && !souple && dansTableau && !apresTotal
        ? ligne.match(/^(.*?[A-Za-zÀ-ÿ].*?)\s{2,}(-?\d[\d'’]*(?:[.,]\d{2}|[.,][-–—]))$/)
        : null;
    if (
      forfait &&
      !PAS_UNE_PRESTATION.test(forfait[1]) &&
      !/remise|r[ée]duction|d[ée]duction|instructions|paiement/i.test(forfait[1])
    ) {
      const p = valeur(forfait[2]);
      const { d, sd } = titreEtDetail(designation(forfait[1]), numerotee);
      if (p > 0 && d.length > 1) {
        derniere = { d, sd, q: 1, u: 'forfait', p, rem: 0 };
        articles.push(derniere);
        oublieLAttente();
        continue;
      }
    }
    if (souple) {
      const { d, sd } = titreEtDetail(souple.d || '', numerotee);
      if (d.length > 1 && !/^\d+$/.test(d)) {
        derniere = { ...souple, d, sd };
        const c = coupe(brute);
        if (c && c.droite) {
          derniere.ref = c.gauche;
          derniere.des = designation(lisLigneSouple(auPropre(c.droite), dansTableau)?.d || '');
        }
        articles.push(derniere);
      }
      oublieLAttente();
      continue;
    }
    if (m) {
      const p = nombre(m[4]);
      const q = nombre(m[2]);
      const lu = titreEtDetail(designation(m[1]), numerotee);
      let d = lu.d;
      if (d.length > 1 && q > 0 && p !== 0 && !/^\d+$/.test(d) && !PAS_UNE_PRESTATION.test(d)) {
        let rem = m[5] && /%/.test(m[5]) ? nombre(m[5]) : 0;
        // Le pourcentage est la TVA de la ligne quand le total se calcule sans lui.
        if (
          rem &&
          m[6] &&
          !egal(q * p * (1 - rem / 100), nombre(m[6])) &&
          egal(q * p, nombre(m[6]))
        )
          rem = 0;
        // Colonne « Unité » avant la quantité : l'unité finit la désignation.
        let u = m[3] || '';
        const finUnite = !u && d.match(UNITE_FINALE);
        // « Enduit 25 kg » : l'unité qui suit un nombre est une mesure du nom.
        if (finUnite && finUnite.index > 1 && !/\d$/.test(d.slice(0, finUnite.index))) {
          u = finUnite[1];
          d = d.slice(0, finUnite.index).trim();
        }
        derniere = { d, sd: lu.sd, q, u: (u || 'pce').toLowerCase(), p, rem };
        const c = coupe(brute);
        if (c && c.droite) {
          // « Plafond » à gauche, « Existant 40 55.00 … » à droite.
          derniere.ref = c.gauche;
          derniere.des = designation(auPropre(c.droite).match(LIGNE)?.[1] || '');
        }
        articles.push(derniere);
      }
      oublieLAttente();
      continue;
    }
    // Une ligne de texte seul : la désignation d'une ligne dont les chiffres
    // suivent, ou le détail de la prestation précédente.
    const commenceParPosition = numerotee;
    if (enAttente && !derniere && !titreNumerote && /^\p{Ll}/u.test(ligne)) {
      // La suite d'un titre écrit sur deux lignes.
      enAttente += ' ' + ligne.replace(/\s+/g, ' ');
    } else if (derniere && !derniere.sd && derniere.ref === undefined && /^\p{Ll}/u.test(ligne)) {
      // La fin du titre, écrite à la ligne : « … (douche à » / « l'italienne… ».
      derniere.d = derniere.d + ' ' + ligne.replace(/\s+/g, ' ');
    } else if (derniere && !commenceParPosition && ouvreUnTitre(indice)) {
      // Le titre de la prestation suivante, dont les chiffres sont posés sur la
      // description, juste en dessous.
      enAttente = designation(ligne);
      titreNumerote = false;
      descriptionAttente = '';
      derniere = null;
    } else if (enAttente && titreNumerote && !commenceParPosition && !derniere) {
      // La suite du titre numéroté (en minuscule), ou sa description.
      const texte = ligne.replace(/\s+/g, ' ');
      if (!descriptionAttente && /^\p{Ll}/u.test(texte)) enAttente += ' ' + texte;
      else descriptionAttente = ajouteDetail(descriptionAttente, texte, retrait);
    } else if (commenceParPosition || !derniere) {
      enAttente = designation(ligne);
      titreNumerote = commenceParPosition;
      descriptionAttente = '';
      derniere = null;
    } else {
      const c = derniere.ref !== undefined ? coupe(brute) : null;
      if (c) {
        // La suite du titre, à gauche ; le détail, à droite.
        derniere.ref = [derniere.ref, c.gauche].filter(Boolean).join(' ');
        if (c.droite) derniere.sd = ajouteDetail(derniere.sd, c.droite);
      } else {
        derniere.sd = ajouteDetail(derniere.sd, ligne, retrait);
      }
    }
  }
  // Titre recomposé : « Plafond » + « Existant » = « Plafond existant ».
  return (
    articles
      .map(({ ref, des, ...a }) =>
        ref && des && CODE_ARTICLE.test(ref.trim())
          ? // « 401234 » à gauche est le numéro d'article du fournisseur :
            // il reste devant le nom, qui garde sa majuscule.
            { ...a, d: ref.trim() + ' ' + des }
          : ref
            ? { ...a, d: [ref, enMinuscule(des || '')].filter(Boolean).join(' ') }
            : a,
      )
      .map((a) => (a.sd ? { ...a, sd: a.sd.replace(/\u02bc/g, '’') } : a))
      // Un numéro d'article en colonne laissait ses espaces dans le nom.
      .map((a) => ({
        ...a,
        d: String(a.d || '')
          .replace(/\s+/g, ' ')
          .trim(),
      }))
  );
}

/** Segments d'une ligne, séparés par trois espaces ou plus, avec leur colonne. */
const segments = (ligne) => {
  const sortie = [];
  const re = /\S+(?:\s{1,2}\S+)*/g;
  let m;
  while ((m = re.exec(ligne))) sortie.push({ col: m.index, texte: m[0] });
  return sortie;
};

/**
 * Le numéro d'une ancienne pièce : « N° de facture : 245 », « Facture n° 245 »,
 * « Facture 02 »… Le mot cherché ne déborde pas sur la ligne suivante, et une
 * date n'est jamais un numéro : avec « Date de facture : 12.03.2025 » écrit
 * avant le numéro, c'est la date qui était reprise comme numéro.
 *
 * @param {string} texte en-tête de la pièce
 * @returns {string} le numéro, ou '' s'il n'est pas lu
 */
export function numeroDeLaPiece(texte) {
  const VALEUR = '([A-Z]{0,4}[-–]?\\d[\\w\\-/.]*)';
  const motifs = [
    new RegExp(
      '\\b(?:n[°o]\\.?|num[ée]ro|nr\\.?)[ \\t]*(?:de[ \\t]+)?(?:la[ \\t]+)?(?:facture|offre|devis)[ \\t]*:?[ \\t]*' +
        VALEUR,
      'gi',
    ),
    new RegExp(
      '\\b(?:facture|offre|devis)[ \\t]*(?:n[°o]\\.?|num[ée]ro|nr\\.?)?[ \\t]*:?[ \\t]*' + VALEUR,
      'gi',
    ),
  ];
  const estUneDate = (v) => /^\d{1,2}[./]\d{1,2}[./]\d{2,4}$/.test(v);
  for (const motif of motifs) {
    for (const m of String(texte || '').matchAll(motif)) {
      const v = m[1].replace(/[.,;:]+$/, '');
      if (!estUneDate(v)) return v;
    }
  }
  return '';
}

/** Une ligne qui ne porte que la civilité : « Monsieur », « Mme », « Famille »… */
const CIVILITE =
  /^(?:monsieur(?:\s+et\s+madame)?|madame(?:\s+et\s+monsieur)?|mademoiselle|messieurs|mesdames|famille|m\.|mme\.?|mlle\.?|mr\.?|mrs\.?|herr|frau|familie)$/i;

/**
 * Rattache une civilité seule sur sa ligne au nom qui la suit : sans cela,
 * le client « Monsieur / Jean Dupont / Rue… » s'appelait « Monsieur ».
 *
 * @param {string[]} liste lignes d'un bloc d'adresse
 * @returns {string[]} les lignes, civilité et nom réunis
 */
const fusionneCivilite = (liste) => {
  const sortie = [];
  for (let i = 0; i < liste.length; i += 1) {
    if (CIVILITE.test(String(liste[i] || '').trim()) && liste[i + 1]) {
      sortie.push(liste[i].trim() + ' ' + liste[i + 1].trim());
      i += 1;
    } else sortie.push(liste[i]);
  }
  return sortie;
};

/** Texte comparable : sans casse, accents ni ponctuation. */
const comparable = (t) =>
  String(t || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Le client d'une ancienne pièce : après « Facturé à », « Client »… ou, sur un
 * PDF, le bloc d'adresse à droite de l'en-tête, qui n'est pas celui de
 * l'entreprise.
 *
 * @param {string[]} entete lignes avant le tableau
 * @param {string} [npaSociete] NPA de l'entreprise, pour ne pas la prendre pour le client
 * @param {{nom?: string, adr?: string}} [societe] nom et rue de l'entreprise :
 *   un client de la même localité qu'elle n'est pas écarté pour autant
 * @returns {{nom: string, adr: string, npa: string}} le client, champs vides à défaut
 */
export function lisClient(entete, npaSociete = '', societe = {}) {
  // La ligne NPA d'un bloc est-elle celle de l'entreprise ? Le même NPA ne
  // suffit pas — beaucoup de clients habitent la commune de l'entreprise, et
  // leur adresse était écartée ; il faut aussi sa rue ou son nom dans le bloc.
  const rueSoc = comparable(societe.adr);
  const nomSoc = comparable(societe.nom);
  const estLaSociete = (bloc, i) => {
    // Le nom ou la rue de l'entreprise juste au-dessus : c'est elle, quel que
    // soit le NPA (une ancienne adresse, une autre commune).
    const parNom = bloc
      .slice(Math.max(0, i - 3), i)
      .some(
        (x) => (rueSoc && comparable(x) === rueSoc) || (nomSoc && comparable(x).includes(nomSoc)),
      );
    if (parNom) return true;
    if (!npaSociete || codePostal(bloc[i]) !== codePostal(npaSociete)) return false;
    return !rueSoc && !nomSoc;
  };
  const lignes = entete.map((l) => l.replace(/\u00a0/g, ' '));
  const nettes = lignes.map((l) => l.trim()).filter(Boolean);
  // 1. Un libellé l'annonce. Sur un PDF lu colonnes conservées, il partage
  // sa ligne avec d'autres colonnes : seul le texte de sa colonne compte, et
  // les lignes suivantes se lisent dans la même colonne.
  // « Facturer à » est celui des factures d'Invoice2go : sans lui, le client
  // d'une facture reprise restait vide (« Facturé à » seul était reconnu).
  const LIBELLE =
    /factur(?:[ée]{0,2}|er)\s*[àa]|^client\b|à l'attention|destinataire|adress[ée]{0,2}\s*[àa]|bill\s*to|invoice\s*to|^[àa]\s*:/i;
  const RETIRE =
    /.*?(factur(?:[ée]{0,2}|er)\s*[àa]|client|à l'attention de|à l'attention|destinataire|adress[ée]{0,2}\s*[àa]|bill\s*to|invoice\s*to|^[àa](?=\s*:))\s*:?\s*/i;
  for (let i = 0; i < lignes.length; i += 1) {
    const segs = segments(lignes[i]);
    const k = segs.findIndex((x) => LIBELLE.test(x.texte));
    if (k < 0) continue;
    let reste = segs[k].texte.replace(RETIRE, '').trim();
    let col = segs[k].col + segs[k].texte.length - reste.length;
    if (!reste && segs[k + 1]) {
      reste = segs[k + 1].texte;
      col = segs[k + 1].col;
    }
    const suite = [reste];
    for (let j = i + 1; j < lignes.length && suite.length < 4; j += 1) {
      const s2 = segments(lignes[j]);
      if (!s2.length) continue;
      const proche = s2.reduce((m, x) => (Math.abs(x.col - col) < Math.abs(m.col - col) ? x : m));
      if (Math.abs(proche.col - col) > 12 && s2.length > 1) continue;
      suite.push(s2.length > 1 ? proche.texte : s2[0].texte);
    }
    const nets = fusionneCivilite(suite.filter(Boolean));
    const iNpa = nets.findIndex((x) => NPA.test(x));
    return {
      nom: nets[0] || '',
      adr: iNpa > 1 ? nets[iNpa - 1] : nets[1] || '',
      npa: iNpa >= 0 ? nets[iNpa] : '',
    };
  }
  // 2. Le bloc sous le titre « Facture » (ou « Offre », « Devis ») : Invoice2go
  // y pose le client sans libellé, à gauche. Le dernier titre seul compte —
  // le premier « FACTURE » est le filigrane, au-dessus de l'entreprise — et
  // le client peut habiter la même commune que l'entreprise.
  let titre = -1;
  lignes.forEach((l, i) => {
    if (/^(facture|offre|devis)$/i.test(l.trim())) titre = i;
  });
  if (titre >= 0) {
    const bloc = [];
    for (let j = titre + 1; j < lignes.length && bloc.length < 6; j += 1) {
      const s = segments(lignes[j]);
      if (!s.length) {
        if (bloc.length) break;
        continue;
      }
      bloc.push(s[0].texte.replace(/[,;]\s*$/, '').trim());
    }
    const blocNet = fusionneCivilite(bloc);
    const iNpa = blocNet.findIndex((x) => NPA.test(x));
    if (iNpa >= 1 && iNpa <= 3) {
      return {
        nom: blocNet[0],
        adr: iNpa >= 2 ? blocNet[iNpa - 1] : '',
        npa: blocNet[iNpa],
      };
    }
  }
  // 3. Le bloc d'adresse de droite (PDF lu colonnes conservées).
  const droite = [];
  for (const l of lignes) {
    const s = segments(l);
    const d = s.filter((x) => x.col >= 40);
    if (
      d.length &&
      !/page\s+\d|établi par|n° tva|tva|date|échéance|^(facture|offre|devis)\b/i.test(d[0].texte)
    )
      droite.push(d[0].texte);
    else if (droite.length && !d.length && s.length) break;
  }
  const blocDroite = fusionneCivilite(droite);
  const blocs = [blocDroite, fusionneCivilite(nettes)];
  for (const bloc of blocs) {
    const iNpa = bloc.findIndex((x, i) => NPA.test(x) && !estLaSociete(bloc, i));
    if (iNpa >= 1) {
      const debut = bloc === blocDroite ? 0 : Math.max(0, iNpa - 2);
      return {
        nom: iNpa - debut >= 2 ? bloc[debut] : bloc[iNpa - 1],
        adr: iNpa - debut >= 2 ? bloc[iNpa - 1] : '',
        npa: bloc[iNpa],
      };
    }
  }
  return { nom: '', adr: '', npa: '' };
}

/**
 * Lit une ancienne pièce.
 *
 * @param {string} texte texte collé ou lu dans le PDF
 * @param {{npaSociete?: string, nomSociete?: string, adrSociete?: string}} [options]
 *   NPA, nom et rue de l'entreprise, à ne pas prendre pour le client
 * @returns {{genre: 'offre'|'facture'|'', numero: string, date: string,
 *   client: {nom: string, adr: string, npa: string},
 *   lignes: {d: string, sd: string, q: number, u: string, p: number, rem: number}[],
 *   remisePct: number, acomptePct: number, totalTtc: number, echeance: string,
 *   delaiJours: number, paye: number, remarques: string}} ce qui a été lu
 */
export function lisPiece(texte, { npaSociete = '', nomSociete = '', adrSociete = '' } = {}) {
  const lignes = String(texte || '')
    .replace(/\r/g, '')
    .split('\n');
  const iTableau = lignes.findIndex(
    (l) => EN_TETE_TABLEAU.test(l) && /qt|quantit|prix|p\.u|total|montant/i.test(l),
  );
  const entete = iTableau >= 0 ? lignes.slice(0, iTableau) : lignes;
  const corps = iTableau >= 0 ? lignes.slice(iTableau + 1) : lignes;
  const tout = entete.join('\n');
  const numero = numeroDeLaPiece(tout);
  const date =
    (tout.match(/\bdate\s*:?\s*(\d{1,2}[./]\d{1,2}[./]\d{2,4})/i) ||
      tout.match(/\b(\d{1,2}\.\d{1,2}\.\d{4})\b/) ||
      [])[1] || '';
  // Colonne de la désignation, quand une colonne « Réf. » la précède.
  const enTete = iTableau >= 0 ? lignes[iTableau].replace(/\u00a0/g, ' ') : '';
  const iDes = enTete.search(/d[ée]signation|description/i);
  const colonne = iDes > 0 && /\S/.test(enTete.slice(0, iDes)) ? iDes : 0;
  // Remise globale et acompte, au pied de la pièce.
  const pied = corps.map(auPropre).join('\n');
  const sousTotal = nombre((pied.match(/sous-total\s+(-?\d[\d'’]*[.,]\d{2})/i) || [])[1]);
  const remise = Math.abs(
    nombre((pied.match(/\bremise\b[^\n\d-]*(-?\s*\d[\d'’]*[.,]\d{2})/i) || [])[1]),
  );
  // À huit décimales, comme la base : arrondie à deux, la remise d'une offre
  // de 39'000.– en perdait 1.52.
  const remisePct =
    sousTotal > 0 && remise > 0 && remise < sousTotal
      ? Math.round((remise / sousTotal) * 1e10) / 1e8
      : 0;
  // Le total TTC de l'ancienne pièce : la reprise doit retomber dessus.
  // « Total TTC », « Montant de la facture (taxe comprise) », « Total TVA
  // incluse », « Total inkl. MwSt »…
  const totaux = [
    ...pied.matchAll(
      /(?:total\s+ttc|montant\s+(?:de\s+la\s+facture|total)[^\n\d]*(?:ttc|taxe\s+comprise|tva\s+(?:comprise|incluse))|total[^\n\d]*(?:taxe\s+comprise|tva\s+(?:comprise|incluse))|total[^\n\d]*inkl\.?\s*mwst)[^\n\d]*?(?:chf\s*)?(-?\d{1,3}(?:[ '’\u00a0]?\d{3})*[.,]\d{2})/gi,
    ),
  ];
  const totalTtc = totaux.length ? nombre(totaux[totaux.length - 1][1]) : 0;
  // Le total hors taxe, quand la pièce le donne : « Total HT », « Sous-total »,
  // « Montant HT » ; à défaut, un « Total » seul.
  const MONTANT_PIED = "(-?\\d{1,3}(?:[ '’\\u00a0]?\\d{3})*[.,]\\d{2})";
  const totalHt =
    sousTotal ||
    nombre(
      (pied.match(
        new RegExp(
          '(?:total|montant)\\s+(?:ht|hors\\s+taxes?|net)\\s*:?\\s*(?:chf\\s*)?' + MONTANT_PIED,
          'i',
        ),
      ) || [])[1],
    );
  const totalSeul = nombre(
    (pied.match(new RegExp('^\\s*(?:total|montant)\\s*:?\\s*(?:chf\\s*)?' + MONTANT_PIED, 'im')) ||
      [])[1],
  );
  const acomptePct = nombre(
    (pied.match(/acompte[^%\n]{0,40}?(\d{1,3}(?:[.,]\d+)?)\s*%/i) || [])[1],
  );
  // L'échéance de l'ancienne facture : une date (« Échéance : 27.09.2026 »,
  // « À payer avant le… »), ou un délai (« payable à 30 jours »).
  const avecPied = tout + '\n' + pied;
  const DATE = '(\\d{1,2}[./]\\d{1,2}[./]\\d{2,4})';
  const echeance =
    (avecPied.match(new RegExp('[ée]ch[ée]ance\\s*:?\\s*' + DATE, 'i')) ||
      avecPied.match(
        new RegExp(
          "(?:payable|à payer|a payer|à régler|a regler)\\s+(?:avant|jusqu['’]?au|au plus tard)\\s+(?:le\\s+)?" +
            DATE,
          'i',
        ),
      ) ||
      avecPied.match(new RegExp('due\\s*date\\s*:?\\s*' + DATE, 'i')) ||
      [])[1] || '';
  const delai = nombre(
    (avecPied.match(/(?:payable|paiement|délai|conditions?|net)[^\n\d]{0,30}?(\d{1,3})\s*jours/i) ||
      [])[1],
  );
  // Une facture déjà réglée : « Payé 2'945.29 » et « Net à payer 0.00 ».
  const MONTANT = "(-?\\d{1,3}(?:[ '’\\u00a0]?\\d{3})*[.,]\\d{2})";
  const paye = nombre((pied.match(new RegExp('^\\s*pay[ée]e?\\s+' + MONTANT, 'im')) || [])[1]);
  const netLu = pied.match(new RegExp('net\\s+[àa]\\s+payer\\s+' + MONTANT, 'i'));
  const netAPayer = netLu ? nombre(netLu[1]) : null;
  // Les remarques de l'ancienne pièce (« Messages / remarques »), pied de
  // page répété exclu : elles suivent la pièce reprise.
  const iRemarques = corps.findIndex((l) => /^\s*messages?\s*\/\s*remarques?\s*$/i.test(l));
  const remarques =
    iRemarques < 0
      ? ''
      : corps
          .slice(iRemarques + 1)
          .map((l) => l.trim())
          .filter((l) => !PIED_DE_PAGE.test(l))
          .join('\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
  const genre = /\bfacture\b/i.test(tout)
    ? 'facture'
    : /\b(offre|devis)\b/i.test(tout)
      ? 'offre'
      : '';
  // Le client : dans l'en-tête d'abord ; sinon dans tout le document, une
  // adresse placée sous le tableau ou un en-tête mal découpé.
  const societe = { nom: nomSociete, adr: adrSociete };
  let client = lisClient(entete, npaSociete, societe);
  if (!client.nom && !client.adr && iTableau >= 0) {
    client = lisClient(lignes, npaSociete, societe);
  }
  // Les articles ; aucun reconnu mais un montant lisible : une ligne porte ce
  // montant, pour que la reprise ne reste jamais à zéro. À corriger au besoin.
  // La colonne « Position » de l'en-tête, et les lignes répétées à chaque page.
  const enTetePos = enTete.match(/^(\s*)(?:pos\b|pos\.|position|n°|no\.)/i);
  const colPosition = enTetePos ? enTetePos[1].length : null;
  const compte = new Map();
  lignes.forEach((l) => {
    const t = auPropre(l.replace(/\u00a0/g, ' ')).trim();
    if (t.length > 40) compte.set(t, (compte.get(t) || 0) + 1);
  });
  const repetees = new Set([...compte].filter(([, n]) => n >= 2).map(([t]) => t));
  let articles = lisLignes(corps, iTableau >= 0, { colonne, colPosition, repetees });
  if (!articles.length) {
    const tauxLu = nombre((pied.match(/tva[^\n%]{0,20}?(\d{1,2}(?:[.,]\d+)?)\s*%/i) || [])[1]);
    const ht =
      totalHt ||
      (totalTtc ? Math.round((totalTtc / (1 + (tauxLu || 0) / 100)) * 100) / 100 : 0) ||
      totalSeul;
    if (ht > 0) {
      // La description des travaux, quand la pièce en porte une : les lignes
      // de texte entre l'adresse du client et les totaux.
      const iNpaClient = client.npa ? lignes.findIndex((l) => l.trim() === client.npa.trim()) : -1;
      const iTotal = lignes.findIndex(
        (l, i) => i > iNpaClient && /^\s*(sous-)?total|^\s*montant/i.test(l),
      );
      const description = lignes
        .slice(iNpaClient + 1, iTotal > iNpaClient ? iTotal : undefined)
        .map((l) => l.trim())
        .filter(
          (l) =>
            (l.match(/[A-Za-zÀ-ÿ]/g) || []).length >= 8 &&
            !/total|tva|montant|date|^facture|^offre|^devis|page|iban|merci|échéance|payable/i.test(
              l,
            ),
        )
        .slice(0, 2)
        .join(' — ')
        .slice(0, 200);
      articles = [
        {
          d:
            description ||
            (genre === 'offre' ? 'Travaux selon offre' : 'Travaux selon facture') +
              (numero ? ' n° ' + numero : ''),
          q: 1,
          u: 'forfait',
          p: ht,
          rem: 0,
        },
      ];
    }
  }
  return {
    genre,
    numero,
    date: date ? date.replace(/\//g, '.') : '',
    client,
    lignes: articles,
    remisePct,
    acomptePct: acomptePct > 0 && acomptePct <= 100 ? acomptePct : 0,
    totalTtc,
    echeance: echeance.replace(/\//g, '.'),
    delaiJours: delai > 0 && delai <= 365 ? delai : 0,
    paye: paye > 0 && (netAPayer === null || netAPayer <= 0) ? paye : 0,
    remarques,
  };
}
